import React, { useEffect, useMemo, useRef, useState } from 'react';

// Who owns a debrief action.
//
// Rob, 2026-08-14: "Action owners should bring up a list of users within the
// supply pilot (like how the comments section works you can '@' someone)."
//
// FREE TEXT SURVIVES, and that is the whole design. An owner is often a team
// ("the AV team") or an outside company ("DZA"), and forcing every owner to be
// a Supply Pilot user would make the field unusable for half its real uses. So
// this is a text input that OFFERS people, rather than a picker that only
// accepts them.
//
// Picking somebody sets `ownerUserId` alongside the text. That is what unblocks
// "notify each action owner when the debrief publishes": a free-text owner
// simply doesn't notify, and now that's a property of the row instead of an
// unanswerable question.
//
// Deliberately NOT the comments' sentinel format (`@[u:uuid:Name]`). That
// exists because a comment is prose with names embedded mid-sentence. An owner
// is a single value, so a plain string plus an id is the honest shape — storing
// a sentinel here would mean parsing it back out everywhere the owner is shown.
export default function ActionOwnerInput({
  value,              // the free-text owner
  ownerUserId,        // set when it resolves to an internal user
  users = [],         // [{ id, full_name, email }]
  onCommit,           // ({ owner, ownerUserId }) => void
  disabled = false,
  placeholder = 'Owner — a person, a team, or an outside company',
  className = '',
}) {
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => { setText(value || ''); }, [value]);

  // The query is whatever follows a trailing "@", so typing "@ca" narrows and
  // typing "the AV team" never opens the list at all.
  const query = useMemo(() => {
    const at = text.lastIndexOf('@');
    if (at < 0) return null;
    const after = text.slice(at + 1);
    // A space ends the mention — "@Sam Rivera" is one name, but "@AV team of"
    // shouldn't keep searching forever.
    return after.length <= 40 ? after.toLowerCase() : null;
  }, [text]);

  const matches = useMemo(() => {
    if (query == null) return [];
    const q = query.trim();
    const pool = users.filter(u => u.is_active !== false);
    if (!q) return pool.slice(0, 6);
    return pool.filter(u =>
      String(u.full_name || '').toLowerCase().includes(q)
      || String(u.email || '').toLowerCase().includes(q)
    ).slice(0, 6);
  }, [query, users]);

  useEffect(() => { setHighlight(0); }, [query]);

  // Close on an outside click, so the list can't hang over the page.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const showList = open && query != null && matches.length > 0;

  const choose = (user) => {
    const at = text.lastIndexOf('@');
    const name = user.full_name || user.email || 'User';
    const next = (at >= 0 ? text.slice(0, at) : text) + name;
    setText(next);
    setOpen(false);
    onCommit?.({ owner: next.trim() || null, ownerUserId: user.id });
  };

  // Typing after a pick BREAKS the link, deliberately. If someone picks Sam and
  // then edits it to "Sam's team", the row must stop claiming it can notify
  // Sam — a notification going to the wrong person is worse than none.
  const commitText = () => {
    const clean = text.trim();
    if (clean === (value || '') ) return;
    const stillMatches = ownerUserId
      && users.some(u => u.id === ownerUserId && (u.full_name || u.email) === clean);
    onCommit?.({ owner: clean || null, ownerUserId: stillMatches ? ownerUserId : null });
  };

  const onKeyDown = (e) => {
    if (!showList) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => (h + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => (h - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(matches[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        value={text}
        disabled={disabled}
        onChange={e => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={commitText}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label="Action owner"
        // A bare input is an implicit textbox, and aria-expanded isn't valid on
        // one — the combobox role is what carries it. Setting the role also
        // makes the listbox relationship real rather than visual-only.
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        className="w-full bg-white border border-line rounded-lg px-3 py-1.5 text-[13px] font-['Metropolis'] focus:outline-none focus:border-[#92D400] disabled:opacity-60" />

      {/* The link is shown, not implied. Without it there is no way to tell an
          owner who will be notified from one who won't. */}
      {ownerUserId && !showList && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold uppercase tracking-[0.12em] text-fusion-forest bg-[#92D400]/12 border border-[#92D400]/40 rounded-full px-2 py-0.5 pointer-events-none">
          Will be notified
        </span>
      )}

      {showList && (
        <ul role="listbox" className="absolute z-20 left-0 right-0 mt-1 bg-white border border-line rounded-lg shadow-lift max-h-52 overflow-auto">
          {matches.map((u, i) => (
            <li key={u.id}>
              <button
                type="button"
                role="option"
                aria-selected={i === highlight}
                // mousedown, not click: blur fires first on click and would
                // commit the half-typed text before the pick registered.
                onMouseDown={e => { e.preventDefault(); choose(u); }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-1.5 font-['Metropolis'] text-[13px] ${i === highlight ? 'bg-[#F2F6E8] text-ink' : 'text-fusion-mid-grey'}`}>
                {u.full_name || u.email}
                {u.full_name && u.email && <span className="text-[11px] text-[#9BA08C]"> · {u.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && query != null && matches.length === 0 && (
        <p className="absolute z-20 left-0 right-0 mt-1 bg-white border border-line rounded-lg px-3 py-1.5 font-['Metropolis'] text-[11px] text-[#9BA08C]">
          Nobody matching — keep typing to name a team or an outside company instead.
        </p>
      )}
    </div>
  );
}
